import { useEffect, useMemo, useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import defaultContent from "../../../content/actionReportsIboOnboarding";
import arDefaultContent from "../../../content/actionReports";
import { useTranslation } from "react-i18next";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import type { BaseABOData } from "../common/ActionReportListView/ActionReportListView";
import type { FieldConfig } from "../common/ActionReportListView/types";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportsTreeView } from "../common/ActionReportTreeView/ActionReportsTreeView";
import type { SearchCallback, SelectionCallback } from "../../../common/components/SearchSelect/SearchSelect";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import { IconInfoCircle } from "../../../common/icons/IconInfoCircle/IconInfoCircle";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import type { IboOnboardingResult } from "../../../services/ActionReports/IboOnboardingAPI.types";
import { useFetchIboOnboardingData } from "./IboOnboardingActionReportDataLayer";
import { toggleSortDirection, setAffAbosToExpand } from "../../../reducers/iboOnboardingArReport";
import { useActionReportTreeView } from "../common/hooks/useActionReportTreeView";
import { getCurrentPeriod } from "../../../common/util/period";
import { formatDateKpi } from "../../../common/util/formatDate";
import { storedLocale } from "../../Authentication/util";
import "./IboOnboardingActionReport.scss";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import {
  iboOnboardingPageViewTrack,
  iboOnboardingSortAscendingTrack,
  iboOnboardingSortDescendingTrack,
  iboOnboardingProfileTrack,
  iboOnboardingInfoTrack,
  iboOnboardingAddToWatchlistTrack,
  iboOnboardingRemoveFromWatchlistTrack,
  iboOnboardingPhoneIconTrack,
  iboOnboardingMessageIconTrack,
} from "../../../common/tealium/ActionReports/IboOnboardingTrack";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { ANA_MARKETS } from "../../../common/constants/market-constants";

type IboOnboardingColumnKey = keyof typeof defaultContent.table.columns;

const BUSINESS_EFFECTIVE_DATE_KPI: IboOnboardingColumnKey = "businessEffectiveDate";
const BUSINESS_ANNIVERSARY_DATE_COLUMN: IboOnboardingColumnKey = "businessAnniversaryDate";
const VCS_SALES_KPI: IboOnboardingColumnKey = "vcsSales";

export const IBO_ONBOARDING_KPIS = [BUSINESS_EFFECTIVE_DATE_KPI, VCS_SALES_KPI];

const formatVcsSales = (result: IboOnboardingResult): string => {
  if (!result.vcsSales) return "--";
  const formatted = result.vcsSales.achievement?.formatted ?? "";
  if (result.vcsSales.achievement?.achieved) {
    return formatted ? `${formatted} ✓` : "✓";
  }
  return formatted || "--";
};

const formatBed = (result: IboOnboardingResult): string => {
  if (!result.businessEffectiveDate) return "--";
  return formatDateKpi(result.businessEffectiveDate, storedLocale());
};

export const mapResultToReportData = (result: IboOnboardingResult): BaseABOData => ({
  aboName: result.aboName,
  aboNumber: result.abo.toString(),
  abo: result.abo.toString(),
  aff: result.aff,
  businessEffectiveDate: formatBed(result),
  vcsSales: formatVcsSales(result),
  phoneNumber: result.phoneNumber,
  level: result.level,
  imageRefUrl: result.imageRefUrl,
});

// Sorts by businessEffectiveDate (asc = oldest first, desc = newest first; nulls last).
export const getSortedResults = (
  results: IboOnboardingResult[],
  sortDirection: "asc" | "desc" = "desc",
): IboOnboardingResult[] => {
  const sorted = [...results];
  sorted.sort((a, b) => {
    const dateA = a.businessEffectiveDate;
    const dateB = b.businessEffectiveDate;
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    const cmp = dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
    return sortDirection === "asc" ? cmp : -cmp;
  });
  return sorted;
};

export default function IboOnboardingActionReport() {
  const { t } = useTranslation(["actionReportsIboOnboarding", "leafAboName", "actionReports"]);
  const dispatch = useAppDispatch();
  const { sortDirection } = useAppSelector((state) => state.iboOnboardingActionReportTracker);

  const currentPeriod = getCurrentPeriod();
  const { iboOnboardingData, isLoading, errorStatus } = useFetchIboOnboardingData(currentPeriod);
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);

  const confidentialUser = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });

  const reportLength = iboOnboardingData.results.length;
  const hasData = !isLoading && !errorStatus && reportLength > 0;
  const controlsDisabled = isLoading || errorStatus || reportLength === 0;

  const allAbos = useMemo(
    () => iboOnboardingData.results.map((r) => ({ aff: r.aff, abo: String(r.abo) })),
    [iboOnboardingData.results],
  );

  const { isTreeView, handleToggle } = useActionReportTreeView({
    isLoading,
    hasData,
    setAffAbosToExpand,
    allAbos,
  });

  useEffect(() => {
    if (currentPeriod) {
      iboOnboardingPageViewTrack(currentPeriod);
    }
  }, [currentPeriod]);

  const handleWatchlistTrack = (isAdding: boolean) => {
    if (isAdding) {
      iboOnboardingAddToWatchlistTrack();
    } else {
      iboOnboardingRemoveFromWatchlistTrack();
    }
  };

  const businessEffectiveDateColumnKey = ANA_MARKETS.includes(iboOnboardingData.results[0]?.isoCountry ?? "")
    ? BUSINESS_ANNIVERSARY_DATE_COLUMN
    : BUSINESS_EFFECTIVE_DATE_KPI;
  const businessEffectiveDateTitle = t(
    `table.columns.${businessEffectiveDateColumnKey}`,
    defaultContent.table.columns[businessEffectiveDateColumnKey],
  ) as string;
  const vcsSalesTitle = t("table.columns.vcsSales", defaultContent.table.columns.vcsSales) as string;

  const fieldConfig: FieldConfig[] = useMemo(
    () => [
      {
        id: BUSINESS_EFFECTIVE_DATE_KPI,
        kpi: BUSINESS_EFFECTIVE_DATE_KPI,
        title: businessEffectiveDateTitle,
      },
      {
        id: VCS_SALES_KPI,
        kpi: VCS_SALES_KPI,
        title: vcsSalesTitle,
      },
      { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
      { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
    ],
    [businessEffectiveDateTitle, vcsSalesTitle],
  );

  const sortedResults = useMemo(
    () => getSortedResults(iboOnboardingData.results, sortDirection),
    [iboOnboardingData.results, sortDirection],
  );

  const reportData: BaseABOData[] = useMemo(
    () => sortedResults.map((result) => mapResultToReportData(result)),
    [sortedResults],
  );

  const handleSortClick = useCallback(() => {
    if (sortDirection === "asc") {
      iboOnboardingSortDescendingTrack();
    } else {
      iboOnboardingSortAscendingTrack();
    }
    dispatch(toggleSortDirection());
  }, [sortDirection, dispatch]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) => {
      if (!hasData) return [];
      return iboOnboardingData.results
        .map((r) => {
          const sublabel = `#${r.abo} | ${getCountryNameFromCode(r.isoCountry, storedLocale())}`;
          return {
            key: `${r.aff}-${r.abo}`,
            label: r.aboName || confidentialUser,
            sublabel,
            aboNumber: r.abo.toString(),
          };
        })
        .filter((item) => item.label.toLowerCase().includes(text.toLowerCase()) || item.aboNumber.includes(text))
        .slice(0, 10)
        .map(({ key, label, sublabel }) => ({ key, label, sublabel }));
    },
    [iboOnboardingData.results, confidentialUser, hasData],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const sorted = getSortedResults(iboOnboardingData.results, sortDirection);
      const index = sorted.findIndex((r) => `${r.aff}-${r.abo}` === key);
      if (index >= 0) {
        setScrollToIndex(index);
        const found = sorted[index];
        setSearchText(found.aboName || confidentialUser);
      }
      const found = iboOnboardingData.results.find((r) => `${r.aff}-${r.abo}` === key);
      return { displayName: found?.aboName || key };
    },
    [iboOnboardingData.results, confidentialUser, sortDirection],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToIndex(undefined);
  }, []);

  const renderContent = () => {
    if (isTreeView) {
      return <ActionReportsTreeView path="iboOnboarding" />;
    }

    if (isLoading) {
      return (
        <div className="IboOnboardingAR__listViewWrapper">
          <ActionReportListViewLoader columns={3} />
        </div>
      );
    }

    if (errorStatus) {
      return <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.General} />;
    }

    if (reportData.length === 0) {
      return (
        <div className="IboOnboardingAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    return (
      <div className="IboOnboardingAR__listViewWrapper">
        <ActionReportListView
          selectedKpis={IBO_ONBOARDING_KPIS}
          reportId="iboOnboardingV2"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          scrollToIndex={scrollToIndex}
          onProfileTrack={iboOnboardingProfileTrack}
          onWatchlistTrack={handleWatchlistTrack}
          onPhoneTrack={iboOnboardingPhoneIconTrack}
          onMessageTrack={iboOnboardingMessageIconTrack}
          showImageBlock={true}
        />
      </div>
    );
  };

  const pageTitle = getCanonicalTitle({ id: "iboOnboarding" }, t, {});

  return (
    <main className="IboOnboardingAR">
      <ActionReportHeader
        title={pageTitle}
        description={
          isLoading ? (
            <span className="IboOnboardingAR__descriptionLoading">
              <span className="IboOnboardingAR__descriptionLoader">
                <LeafRectangleLoader isLoading={true} width="2rem" height="0.75rem" />
              </span>
              {t("title.pageDescription", defaultContent.title.pageDescription as string)}
            </span>
          ) : reportLength > 0 ? (
            <span>
              <strong>{reportLength}</strong>{" "}
              {t("title.pageDescription", defaultContent.title.pageDescription as string)}
            </span>
          ) : (
            t("title.pageDescription", defaultContent.title.pageDescription as string).toString()
          )
        }
        reportLength={reportLength}
        infoButton={
          <button
            type="button"
            onClick={() => {
              iboOnboardingInfoTrack();
              setShowInfoModal(true);
            }}
            className="IboOnboardingAR__infoButton"
            aria-label={t("info.vcsSalesInfoLabel", defaultContent.info.vcsSalesInfoLabel as string)}
          >
            <IconInfoCircle />
          </button>
        }
      />
      <ActionReportToolbar
        reportLength={reportLength}
        treeViewToggle={{
          enabled: true,
          isTreeView: isTreeView,
          onToggle: handleToggle,
          disabled: !hasData,
          label: t("losTreeView", defaultContent.losTreeView) as string,
        }}
        sort={{
          enabled: !isTreeView,
          direction: sortDirection,
          onClick: handleSortClick,
          disabled: controlsDisabled,
        }}
        search={
          !isTreeView
            ? {
                enabled: true,
                searchCallback: handleSearchCallback,
                selectionCallback: handleSelectionCallback,
                searchText: searchText,
                clearFunction: handleClearSearch,
                disabled: controlsDisabled,
                placeholder: t("searchPlaceholder", arDefaultContent.searchPlaceholder, { ns: "actionReports" }),
              }
            : undefined
        }
      />
      <LeafModal
        open={showInfoModal}
        onCloseCallbackFn={() => setShowInfoModal(false)}
        title={t(
          "table.columns.vcsSales",
          (defaultContent.table as { columns: { [key: string]: string } }).columns.vcsSales,
        ).toString()}
        id="iboOnboardingInfoModal"
      >
        <div className="IboOnboardingAR__infoModal">
          <p className="IboOnboardingAR__infoModal__description">
            {t("info.vcsSalesDescription", defaultContent.info.vcsSalesDescription as string)}
          </p>
          <p className="IboOnboardingAR__infoModal__note">
            {t("info.vcsSalesNote", defaultContent.info.vcsSalesNote as string)}
          </p>
        </div>
      </LeafModal>
      <div className="IboOnboardingAR__content">{renderContent()}</div>
      <div>
        <BonusInfoFooter hideDate={true} />
      </div>
    </main>
  );
}
